import { Automation, Trigger } from "../types/v2/Automation";

export const WEEKDAY_KEYS = [
  "sun",
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
] as const;

export type WeekdayKey = (typeof WEEKDAY_KEYS)[number];

export const WEEKDAY_LABELS: Record<WeekdayKey, string> = {
  sun: "Sun",
  mon: "Mon",
  tue: "Tue",
  wed: "Wed",
  thu: "Thu",
  fri: "Fri",
  sat: "Sat",
};

/** Monday–Friday, the set `cadence: "weekdays"` expands to. */
export const WEEKDAY_ONLY_KEYS: readonly WeekdayKey[] = [
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
];

export const SCHEDULE_CADENCES = ["daily", "weekdays", "days"] as const;

export type ScheduleCadence = (typeof SCHEDULE_CADENCES)[number];

export type ScheduleTriggerValue = {
  cadence: ScheduleCadence;
  /** Only meaningful when cadence === "days". */
  days: WeekdayKey[];
  /** 24-hour "HH:MM". */
  time: string;
};

export const DEFAULT_SCHEDULE_TRIGGER_VALUE: ScheduleTriggerValue = {
  cadence: "daily",
  days: [],
  time: "08:00",
};

const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

/**
 * True when the string is a valid 24-hour "HH:MM", zero-padded.
 * @param {unknown} v - The candidate value.
 * @returns {boolean} True when the value is a well-formed time.
 */
export function isValidScheduleTime(v: unknown): v is string {
  return typeof v === "string" && TIME_PATTERN.test(v);
}

function isCadence(v: unknown): v is ScheduleCadence {
  return (SCHEDULE_CADENCES as readonly string[]).includes(v as string);
}

function normalizeDays(v: unknown): WeekdayKey[] {
  if (!Array.isArray(v)) return [];
  const set = new Set(
    v.filter((d): d is WeekdayKey =>
      (WEEKDAY_KEYS as readonly string[]).includes(d as string)
    )
  );
  // Emit in canonical week order so equality checks and display are stable.
  return WEEKDAY_KEYS.filter((d) => set.has(d));
}

/**
 * Serializes a schedule for storage in Trigger.triggerValue.
 * @param {ScheduleTriggerValue} value - The schedule to serialize.
 * @returns {string} The JSON string to store.
 */
export function encodeScheduleTriggerValue(
  value: ScheduleTriggerValue
): string {
  return JSON.stringify(value);
}

/**
 * Parses a stored triggerValue into a schedule. Total and defensive: malformed
 * JSON, missing fields, an unknown cadence, unknown day keys, or a bad time all
 * fall back to defaults. Never throws — a corrupt stored automation must not be
 * able to take down the scanner for an entire shop.
 * @param {string | number | boolean | null | undefined} raw - The stored value.
 * @returns {ScheduleTriggerValue} A valid schedule.
 */
export function parseScheduleTriggerValue(
  raw: string | number | boolean | null | undefined
): ScheduleTriggerValue {
  if (typeof raw !== "string" || raw.trim() === "") {
    return { ...DEFAULT_SCHEDULE_TRIGGER_VALUE };
  }
  try {
    const parsed = JSON.parse(raw) as Partial<ScheduleTriggerValue>;
    return {
      cadence: isCadence(parsed.cadence)
        ? parsed.cadence
        : DEFAULT_SCHEDULE_TRIGGER_VALUE.cadence,
      days: normalizeDays(parsed.days),
      time: isValidScheduleTime(parsed.time)
        ? parsed.time
        : DEFAULT_SCHEDULE_TRIGGER_VALUE.time,
    };
  } catch {
    return { ...DEFAULT_SCHEDULE_TRIGGER_VALUE };
  }
}

/**
 * Whether a schedule is fully specified — used by the builder to block saving a
 * selected-days schedule with no days chosen (which would never fire).
 * @param {ScheduleTriggerValue} value - The schedule to check.
 * @returns {boolean} True when the schedule can fire.
 */
export function isScheduleValueComplete(value: ScheduleTriggerValue): boolean {
  if (!isValidScheduleTime(value.time)) return false;
  return value.cadence !== "days" || value.days.length > 0;
}

/**
 * Renders "HH:MM" as a 12-hour clock time, e.g. "08:00" -> "8:00 AM".
 * @param {string} time - A 24-hour "HH:MM".
 * @returns {string} The 12-hour rendering.
 */
function formatTime12h(time: string): string {
  if (!isValidScheduleTime(time)) return time;
  const [h, m] = time.split(":");
  const hour = Number(h);
  const suffix = hour < 12 ? "AM" : "PM";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${m} ${suffix}`;
}

/**
 * Renders a schedule as a human sentence for the read-only builder display.
 * @param {ScheduleTriggerValue} value - The schedule to render.
 * @returns {string} e.g. "Every weekday at 8:00 AM".
 */
export function formatScheduleTriggerValue(
  value: ScheduleTriggerValue
): string {
  const at = `at ${formatTime12h(value.time)}`;
  if (value.cadence === "daily") return `Every day ${at}`;
  if (value.cadence === "weekdays") return `Every weekday ${at}`;
  if (value.days.length === 0) return "No days selected";
  return `Every ${value.days.map((d) => WEEKDAY_LABELS[d]).join(", ")} ${at}`;
}

/**
 * True when the automation carries at least one onSchedule trigger.
 *
 * Lives in this shared module rather than in functions-v2 so that both
 * functions-v2/src/lib/automations.ts (which uses it to keep the card-write
 * path from firing schedule automations) and
 * functions-v2/src/lib/scheduledAutomations.ts (which imports back from
 * automations.ts) can use one implementation without a circular import.
 * @param {Pick<Automation, "triggers">} automation - The automation to inspect.
 * @returns {boolean} True when this is a scheduled automation.
 */
export function isScheduledAutomation(
  automation: Pick<Automation, "triggers">
): boolean {
  return (automation.triggers ?? []).some(
    (t) => t.triggerEventType === "onSchedule"
  );
}

/**
 * The automation's schedule-defining trigger — the onSchedule trigger with no
 * triggerField. The other onSchedule triggers are card conditionals.
 * @param {Pick<Automation, "triggers">} automation - The automation to inspect.
 * @returns {Trigger | null} The schedule trigger, or null.
 */
export function getScheduleTrigger(
  automation: Pick<Automation, "triggers">
): Trigger | null {
  return (
    (automation.triggers ?? []).find(
      (t) => t.triggerEventType === "onSchedule" && t.triggerField === null
    ) ?? null
  );
}

/**
 * True when a trigger set is "scheduled" (it carries an onSchedule trigger)
 * but has no schedule-DEFINING trigger — i.e. only schedule conditionals
 * survive.
 *
 * Such an automation saves cleanly, is enabled, and reads as configured, yet
 * can never fire on any path: `evaluateTriggers` refuses scheduled automations
 * on the card-write path, and the scanner's `getSchedule()` returns null. This
 * is a save-time guard, so it takes the loose builder shape (`BuilderTrigger`
 * leaves `triggerEventType` optional) rather than a persisted `Trigger`.
 * @param {Array<{triggerEventType?: string | null, triggerField: string | null}>} triggers - The trigger set being edited.
 * @returns {boolean} True when the set can never fire.
 */
export function hasOrphanScheduleConditions(
  triggers: {
    triggerEventType?: string | null;
    triggerField: string | null;
  }[]
): boolean {
  const isScheduled = triggers.some((t) => t.triggerEventType === "onSchedule");
  const hasDefiningTrigger = triggers.some(
    (t) => t.triggerEventType === "onSchedule" && t.triggerField === null
  );
  return isScheduled && !hasDefiningTrigger;
}

/**
 * The parsed schedule for an automation, or null when it has no schedule
 * trigger. Parsing is total, so a corrupt stored value yields defaults rather
 * than throwing.
 * @param {Pick<Automation, "triggers">} automation - The automation to inspect.
 * @returns {ScheduleTriggerValue | null} The schedule, or null.
 */
export function getSchedule(
  automation: Pick<Automation, "triggers">
): ScheduleTriggerValue | null {
  const trigger = getScheduleTrigger(automation);
  if (!trigger) return null;
  return parseScheduleTriggerValue(trigger.triggerValue);
}

/**
 * Action fields the "On a schedule" trigger does not offer.
 *
 * `color` is the "Change card color to" action (`action-007`), which writes the
 * card's own `color` field. Its own catalog tooltip says it changes the color
 * "without being able to reset the change" — so a schedule with no conditionals
 * would permanently overwrite the real color of every open card on the board,
 * with no undo and no record of what the colors used to be.
 *
 * It is excluded rather than merely discouraged because it has a strictly
 * better substitute: "Set card color" (`action-003`) produces the same visible
 * result through `automationAction`, and "Reset automated changes" can undo it.
 * Nothing is lost by removing it from this one trigger.
 *
 * Deliberately a DENYlist, not an allowlist: the whole point of the schedule
 * trigger is that the existing action catalog works unchanged, so a new action
 * should be available on schedules by default and only excluded on purpose.
 */
export const SCHEDULE_EXCLUDED_ACTION_FIELDS: ReadonlySet<string> = new Set([
  "color",
]);

/**
 * Whether an action may be paired with the "On a schedule" trigger.
 * @param {string | null | undefined} actionField - The action's field.
 * @returns {boolean} False when the schedule trigger excludes this action.
 */
export function isActionAllowedOnSchedule(
  actionField: string | null | undefined
): boolean {
  return !SCHEDULE_EXCLUDED_ACTION_FIELDS.has(actionField ?? "");
}

/**
 * True when a trigger set defines a schedule but attaches no card conditionals,
 * so the sweep matches every open card on the board.
 *
 * The conditional test mirrors `scheduleConditionsMet` in
 * functions-v2/src/lib/scheduledAutomations.ts EXACTLY — `triggerObject` is
 * "card" and `triggerField` is non-null. If the two drift, the builder warns
 * about a scope the scanner does not actually apply, or stays silent about one
 * it does. Takes the loose builder shape, so it can run on an unsaved trigger
 * set as well as a persisted one.
 * @param {Array<{triggerObject?: string | null, triggerEventType?: string | null, triggerField: string | null}>} triggers - The trigger set.
 * @returns {boolean} True when the schedule is unscoped.
 */
export function isUnconditionedSchedule(
  triggers: {
    triggerObject?: string | null;
    triggerEventType?: string | null;
    triggerField: string | null;
  }[]
): boolean {
  const hasDefiningTrigger = triggers.some(
    (t) => t.triggerEventType === "onSchedule" && t.triggerField === null
  );
  if (!hasDefiningTrigger) return false;
  const hasConditions = triggers.some(
    (t) => t.triggerObject === "card" && t.triggerField !== null
  );
  return !hasConditions;
}

/** Heading for the unconditioned-schedule scope warning. */
export const SCHEDULE_SCOPE_ALERT_TITLE = "Automation Alert!";

/**
 * Body of the unconditioned-schedule scope warning. Shared by the builder and
 * the saved-automation list so the two cannot drift.
 */
export const SCHEDULE_SCOPE_ALERT_BODY =
  "This trigger runs on a schedule, not on card activity. With no conditions set, these actions will apply to every open card on the board at the scheduled day/time – not just new or matching cards. Add conditions (recommended) to target specific cards, or leave it as is to hit them all.";
